<script lang="ts" setup>
import { ref, toRefs } from 'vue';
import Calendar from 'primevue/calendar';
import ColumnGroup from 'primevue/columngroup';
import Row from 'primevue/row';
import { useUtilizationStore } from '@/store';
import { useDateTime, usePersonTypes } from '@/composables';
import { TimeRatioReportingSPTableInterface } from '@/types';

interface TimeUsageModalPropsInterface {
  timeRatioData: TimeRatioReportingSPTableInterface[];
}

const props = defineProps<TimeUsageModalPropsInterface>();
const { timeRatioData } = toRefs(props);

const utilizationStore = useUtilizationStore();
const maxDate = ref<Date>(useDateTime().getMostRecentSunday());
const minDate = ref<Date>(new Date('2024-01-02'));
const dateSelect = ref<Date>(new Date(utilizationStore.serviceProviderTimeRatioFilterDate));

const onDateSelect = async (value: Date) => {
  dateSelect.value = value;
  await utilizationStore.updateServiceProviderTimeRatioFilterDate(dateSelect.value);
};
</script>

<template>
  <Card class="flex-none flex flex-column">
    <template #header>
      <div class="flex flex-wrap">
        <div class="flex mr-3 pt-2">
          <Icon name="pie_chart" class="material-icons text-color-blue mr-2" />
          <h2>Time Ratio Reporting up to:</h2>
        </div>
        <div class="flex">
          <Calendar
            v-model="dateSelect"
            show-icon
            :disabled-days="[1, 2, 3, 4, 5, 6]"
            icon-display="input"
            class="text-sm"
            date-format="MM dd, yy"
            :max-date="maxDate"
            :min-date="minDate"
            @date-select="onDateSelect"
          />
        </div>
      </div>
    </template>
    <template #content>
      <DataTable
        :value="timeRatioData"
        data-id="contract_id"
        show-gridlines
        responsive-layout="scroll"
        sort-field="indirect_percent"
        :sort-order="-1"
      >
        <ColumnGroup type="header">
          <Row>
            <Column header="Organization" field="organization" sortable :rowspan="2" />
            <Column header="Contract" field="contract_quote_number" sortable :rowspan="2" />
            <Column v-if="!usePersonTypes.isClinician()" header="Type" field="contract_type" sortable :rowspan="2" />
            <Column header="Weeks" field="weeks" sortable :rowspan="2" />
            <Column :colspan="2" header-class="relative justify-content-center flex-none">
              <template #header>
                <span>Billed Direct Time</span>
                <Icon
                  v-tooltip.top="'Time billed for Session, Consultation, Response to Intervention events'"
                  name="info"
                  class="absolute right-0 top-0 mt-2 mr-1 text-gray-400 text-base cursor-pointer"
                />
              </template>
            </Column>
            <Column :colspan="2" header-class="relative justify-content-center flex-none">
              <template #header>
                <span>Billed Indirect Time</span>
                <Icon
                  v-tooltip.top="'Time billed for Caseload Management, Record Review, IEP Meeting events'"
                  name="info"
                  class="absolute right-0 top-0 mt-2 mr-1 text-gray-400 text-base cursor-pointer"
                />
              </template>
            </Column>
            <Column :colspan="2" header-class="relative justify-content-center flex-none">
              <template #header>
                <span>Billed Evaluation Time</span>
                <Icon
                  v-tooltip.top="'Time billed for Evaluation events'"
                  name="info"
                  class="absolute right-0 top-0 mt-2 mr-1 text-gray-400 text-base cursor-pointer"
                />
              </template>
            </Column>
          </Row>
          <Row>
            <Column header="Hrs" field="direct_hours" sortable header-class="justify-content-center flex-none" />
            <Column header="%" field="direct_percent" sortable header-class="justify-content-center flex-none" />
            <Column header="Hrs" field="indirect_hours" sortable header-class="justify-content-center flex-none" />
            <Column header="%" field="indirect_percent" sortable header-class="justify-content-center flex-none" />
            <Column header="Hrs" field="eval_hours" sortable header-class="justify-content-center flex-none" />
            <Column header="%" field="eval_percent" sortable header-class="justify-content-center flex-none" />
          </Row>
        </ColumnGroup>
        <Column field="organization" />
        <Column field="contract_quote_number" />
        <Column v-if="!usePersonTypes.isClinician()" field="contract_type" />
        <Column field="weeks" />
        <Column field="direct_hours" body-class="justify-content-center flex-none" />
        <Column field="direct_percent" body-class="percentage justify-content-center flex-none" />
        <Column field="indirect_hours" body-class="justify-content-center flex-none" />
        <Column field="indirect_percent" body-class="percentage justify-content-center flex-none" />
        <Column field="eval_hours" body-class="justify-content-center flex-none" />
        <Column field="eval_percent" body-class="percentage justify-content-center flex-none" />
        <template #empty>
          <span v-text="'No data to display'" />
        </template>
      </DataTable>
    </template>
  </Card>
</template>

<style scoped lang="scss">
:deep(.p-card-body) {
  .p-paginator-current {
    flex: 1;
    justify-content: flex-end;
  }
}

:deep(.p-datatable) {
  .percentage::after {
    content: '%';
  }
}
</style>
